import {
  LayoutDashboard, Upload, TrendingUp, Wallet,
  DollarSign, Users as UsersIcon, Package, ShoppingCart, Receipt, ClipboardList,
  CalendarDays, BookOpen, Warehouse, Settings, BarChart3, ArrowLeftRight,
  Building2, GitBranch, ChevronDown, FileSpreadsheet, ShieldCheck, UserCog,
  LogOut, KeyRound, CalendarCheck2 as CalendarLock, BookCheck, History, Plus,
} from "lucide-react";
import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import ChangePasswordDialog from "@/components/ChangePasswordDialog";
import QuickAddClientDialog from "@/components/QuickAddClientDialog";

const menuItems = [
  { id: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { id: "upload", label: "Upload Data", icon: Upload, roles: ["admin", "reviewer"] },
  { type: "divider", label: "MIS" },
  { id: "mis-report", label: "MIS Dashboard", icon: BarChart3 },
  { id: "mis-mappings", label: "Reclassification", icon: ArrowLeftRight, roles: ["admin", "reviewer"] },
  { type: "divider", label: "Statements" },
  { id: "profit-loss", label: "Profit & Loss", icon: TrendingUp },
  { id: "balance-sheet", label: "Balance Sheet", icon: Wallet },
  { id: "cash-flow", label: "Cash Flow", icon: DollarSign },
  { id: "gl-dump", label: "GL Dump", icon: BookOpen },
  { id: "ledger-audit", label: "Ledger Audit", icon: ShieldCheck },
  { id: "audit-jvs", label: "Audit JVs", icon: BookCheck },
  { type: "divider", label: "Analytics" },
  { id: "party-expense", label: "Party Expenses", icon: Receipt },
  { id: "party-sales", label: "Party Sales", icon: UsersIcon },
  { id: "party-purchase", label: "Party Purchase", icon: ShoppingCart },
  { id: "item-sales", label: "Item Sales", icon: Package },
  { id: "item-purchase", label: "Item Purchase", icon: ClipboardList },
  { id: "stock-movement", label: "Stock Movement", icon: Package },
  { type: "divider", label: "Data" },
  { id: "trial-balance", label: "Trial Balance", icon: FileSpreadsheet },
  { id: "monthly-closings", label: "Monthly Closings", icon: CalendarDays },
  { id: "closing-stock", label: "Closing Stock", icon: Warehouse, roles: ["admin", "reviewer"] },
  { type: "divider", label: "Admin", roles: ["admin"] },
  { id: "users", label: "Users & Access", icon: UserCog, roles: ["admin"] },
  { id: "month-locks", label: "Month Lock", icon: CalendarLock, roles: ["admin"] },
  { id: "audit-log", label: "Audit Trail", icon: History, roles: ["admin"] },
  { id: "company-manager", label: "Clients", icon: Building2, roles: ["admin"] },
  { id: "exclusion-settings", label: "Exclusions", icon: Settings, roles: ["admin"] },
];

export default function Sidebar({
  activeView, onNavigate, companies = [], selectedCompany, onCompanyChange,
  branchOptions = [], selectedBranch, onBranchChange, onClientsRefresh,
}) {
  const { user, logout, hasRole } = useAuth();
  const [showChangePwd, setShowChangePwd] = useState(false);
  const [showAddClient, setShowAddClient] = useState(false);
  const isAdmin = user && user !== false && user.role === "admin";
  const roleOk = (item) => !item.roles || (user && user !== false && item.roles.includes(user.role));
  const visibleItems = menuItems.filter(roleOk);
  return (
    <aside className="w-60 bg-zinc-950 flex flex-col shrink-0" data-testid="sidebar">
      {/* Brand */}
      <div className="h-14 flex items-center px-5 border-b border-zinc-800">
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 bg-white rounded flex items-center justify-center">
            <BarChart3 className="w-4 h-4 text-zinc-900" />
          </div>
          <span className="text-[15px] font-black text-white tracking-tight" data-testid="app-title"
            style={{ fontFamily: "'Chivo', system-ui, sans-serif" }}>MIS Platform</span>
        </div>
      </div>

      {/* Client Selector */}
      <div className="px-3 py-3 border-b border-zinc-800">
        <div className="flex items-center justify-between mb-1 px-0.5">
          <div className="text-[9px] font-bold uppercase tracking-[0.15em] text-zinc-500">Client</div>
          {isAdmin && (
            <button
              type="button"
              onClick={() => setShowAddClient(true)}
              data-testid="sidebar-add-client-btn"
              title="Add new client"
              className="flex items-center gap-0.5 text-[9px] font-bold uppercase tracking-[0.1em] text-zinc-400 hover:text-white transition"
            >
              <Plus className="w-2.5 h-2.5" /> Add
            </button>
          )}
        </div>
        <div className="relative">
          <select
            value={selectedCompany || "default"}
            onChange={(e) => onCompanyChange(e.target.value)}
            className="w-full text-[12px] font-medium text-zinc-200 bg-zinc-900 border border-zinc-700 rounded-md px-3 py-1.5 pr-8 appearance-none focus:outline-none focus:ring-1 focus:ring-zinc-500 cursor-pointer"
            data-testid="company-selector"
          >
            {companies.map((c) => (
              <option key={c.company_id} value={c.company_id}>{c.name}</option>
            ))}
          </select>
          <ChevronDown className="w-3.5 h-3.5 text-zinc-500 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
        </div>
        {branchOptions.length > 0 && (
          <div className="relative mt-1.5">
            <GitBranch className="w-3 h-3 text-zinc-500 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
            <select
              value={selectedBranch || ""}
              onChange={(e) => onBranchChange(e.target.value || null)}
              className="w-full text-[11px] text-zinc-400 bg-zinc-900 border border-zinc-700 rounded-md pl-7 pr-3 py-1 appearance-none focus:outline-none focus:ring-1 focus:ring-zinc-500"
              data-testid="branch-selector"
            >
              <option value="">All Branches</option>
              {branchOptions.map((b) => (
                <option key={b.branch_id} value={b.branch_id}>{b.name}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-2 overflow-y-auto">
        {visibleItems.map((item, index) => {
          if (item.type === "divider") {
            return (
              <div key={`div-${index}`} className="mt-5 mb-1.5 px-5">
                <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-600">
                  {item.label}
                </span>
              </div>
            );
          }
          const Icon = item.icon;
          const isActive = activeView === item.id;
          return (
            <button
              key={item.id}
              onClick={() => onNavigate(item.id)}
              data-testid={`nav-${item.id}`}
              className={`w-full sidebar-item ${isActive ? "active" : ""}`}
            >
              <Icon className="w-4 h-4 shrink-0" />
              <span>{item.label}</span>
            </button>
          );
        })}
      </nav>

      {/* Footer: user */}
      <div className="px-3 py-3 border-t border-zinc-800 space-y-2">
        {user && user !== false && (
          <>
            <div className="px-2 py-1">
              <div className="text-[11px] text-zinc-300 font-medium truncate" data-testid="sidebar-user-name">
                {user.name || user.email}
              </div>
              <div className="text-[10px] text-zinc-500 uppercase tracking-wider" data-testid="sidebar-user-role">
                {user.role}
              </div>
            </div>
            <div className="flex gap-1">
              <button
                onClick={() => setShowChangePwd(true)}
                className="flex-1 flex items-center justify-center gap-1 text-[11px] text-zinc-400 hover:text-white bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 rounded px-2 py-1.5 transition"
                data-testid="sidebar-change-password"
              >
                <KeyRound className="w-3 h-3" /> Password
              </button>
              <button
                onClick={logout}
                className="flex-1 flex items-center justify-center gap-1 text-[11px] text-zinc-400 hover:text-white bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 rounded px-2 py-1.5 transition"
                data-testid="sidebar-logout"
              >
                <LogOut className="w-3 h-3" /> Sign out
              </button>
            </div>
          </>
        )}
      </div>
      <ChangePasswordDialog open={showChangePwd} onOpenChange={setShowChangePwd} />
      <QuickAddClientDialog
        open={showAddClient}
        onOpenChange={setShowAddClient}
        onCreated={async (newId) => {
          if (onClientsRefresh) {
            await onClientsRefresh();
            if (newId) onCompanyChange(newId);
          }
        }}
      />
    </aside>
  );
}
